import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// ✅ Import useNavigate
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import CustomNav from '../../components/NavBar/CustomNav';
import { checkvalidEmbedToken } from '../../components/Utilities/checkValidToken';
// ✅ Import generateSlug along with findReportByName
import {
  getCachedUserReports,
  setCachedUserReports,
} from '../../components/Utilities/reportCache';
import {
  findReportByName,
  generateSlug,
} from '../../components/Utilities/slugify';
import { getUserReportsGroupByOrgName } from '../../components/Utilities/v2Apis/getUserReports';
import { addDefaultReport, addReports } from '../../redux/userReducer';

const Home = () => {
  const { groupName, reportName } = useParams();
  // ✅ Initialize navigate
  const navigate = useNavigate();

  const user = useSelector((state) => state.PowerBi.user);
  const reports = useSelector((state) => state.PowerBi.reports);
  const defaultReport = useSelector((state) => state.PowerBi.defaultReport);
  const googleIdToken = useOutletContext();
  // Derived straight from the store, so the sidebar gets its list in the same
  // render as the store update (no extra effect + state round trip).
  const reportsArray = useMemo(
    () =>
      reports.length === 2 &&
      reports[0] &&
      Object.keys(reports[0]).length !== 0 &&
      reports[0].constructor === Object
        ? Object.entries(reports[0])
        : [],
    [reports],
  );
  const [isloadingReports, setLoadingReports] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!user?.ID || !checkvalidEmbedToken(googleIdToken)) {
      setLoadingReports(false);
      return undefined;
    }

    const refreshReports = async () => {
      const result = await getUserReportsGroupByOrgName(user.ID);

      if (Array.isArray(result) && result.length > 0) {
        dispatch(addReports(result));
        setCachedUserReports(user.ID, result);
      }

      setLoadingReports(false);
    };

    const cachedReports = getCachedUserReports(user.ID);
    if (cachedReports && cachedReports.length > 0) {
      dispatch(addReports(cachedReports));
      setLoadingReports(false);
    }

    void refreshReports();
    return undefined;
  }, [dispatch, googleIdToken, user?.ID]);

  useEffect(() => {
    if (!user?.ID || !checkvalidEmbedToken(googleIdToken)) return undefined;

    // Clicking inside a Power BI report moves focus into its iframe, so
    // clicking the sidebar afterwards fires 'focus' again: without a limit this
    // re-fetched the list (and re-rendered every report) on almost every
    // report switch. Refresh at most once a minute.
    let lastRefresh = Date.now();
    const refreshReports = () => {
      if (Date.now() - lastRefresh < 60 * 1000) return;
      lastRefresh = Date.now();
      getUserReportsGroupByOrgName(user.ID).then((result) => {
        if (Array.isArray(result) && result.length > 0) {
          dispatch(addReports(result));
          setCachedUserReports(user.ID, result);
        }
      });
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshReports();
      }
    };

    window.addEventListener('focus', refreshReports);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshReports);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, googleIdToken, user?.ID]);

  useEffect(() => {
    if (
      !isloadingReports &&
      reports.length === 2 &&
      Object.keys(reports[0]).length !== 0 &&
      reports[0].constructor === Object
    ) {
      const grouped = reportsArray;

      let matched = null;
      if (groupName && reportName) {
        matched = findReportByName(grouped, groupName, reportName);
      }

      // ✅ Reworked Fallback Logic: Capture both the report AND its group name
      let fallbackReport = matched;
      let fallbackGroupName = groupName;

      // If no valid URL match was found, pick the very first group and report
      if (!matched && grouped.length > 0) {
        fallbackGroupName = grouped[0][0]; // First group name
        fallbackReport = grouped[0][1][0]; // First report in that group
      }

      if (fallbackReport) {
        // Only the default report is set here. Which report is *selected* is
        // decided by CustomNav from the URL: this page only knows the signed-in
        // user's own report list, so selecting a report here would override a
        // report from another user's list (admin "view as") with this user's
        // first report. Only update when it changed, otherwise a background
        // refresh of the list would re-trigger the embed token request.
        if (fallbackReport.PBIREPORTID !== defaultReport?.PBIREPORTID) {
          dispatch(addDefaultReport(fallbackReport));
        }

        if (!groupName || !reportName) {
          const groupSlug = generateSlug(fallbackGroupName);
          const reportSlug = generateSlug(fallbackReport.ReportName);

          navigate(`/reports/${groupSlug}/${reportSlug}`, { replace: true });
        }
      }
    }
  }, [
    defaultReport,
    dispatch,
    isloadingReports,
    reports,
    reportsArray,
    groupName,
    reportName,
    navigate,
  ]);

  return (
    <Box>
      <Header />
      {/* CustomNav is mounted immediately (not after the reports list loads)
          so the Power BI iframe is bootstrapped and the embed token request
          starts in parallel with the reports API call. Until the report starts
          embedding, the report area shows a small spinner (as the original
          page did while the list loaded). */}
      <CustomNav
        reportsArray={reportsArray}
        reportsLoading={isloadingReports}
        googleIdToken={googleIdToken}
      />
    </Box>
  );
};

export default Home;
